import React from 'react'

import { Box, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { NARROW_COLUMN_WIDTH } from 'config'
import { Header } from 'components/navigation'

/**
 * Loading state for the content detail page (§19). Footprint parity with the
 * loaded page: the wayfinding row + title from the real `Header`, the two header
 * actions, and each card at the field counts it actually holds — mirrors
 * `InternalAnnouncementEditPageSkeleton`'s reasoning and structure.
 *
 * VRG-738 — the trail reads "Market research / Content". The PARENT crumb is the page
 * name and comes from `PAGE_LABELS` (the URL segment is still `content`); "Content" here
 * is the LAST crumb and the title, standing in for the item's own title until it loads —
 * it names the kind of thing (a row of the Content tab), not the parent page, so it
 * deliberately did not become "Market research".
 */
export const InternalMarketContentPageSkeleton = () => (
  <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
    <Header
      title="Content"
      breadcrumbsOveride="Market feed"
      rightComponents={
        <>
          <Skeleton width={92} height={40} />
          <Skeleton width={64} height={40} />
        </>
      }
    />

    <Stack spacing={4}>
      <Card title="Content" spacing={4}>
        <Stack spacing={4}>
          <FieldSkeleton />
          <Stack direction="row" spacing={4}>
            <FieldSkeleton />
            <FieldSkeleton />
          </Stack>
        </Stack>
      </Card>

      <Card title="Outreach" spacing={4}>
        <Stack spacing={4}>
          <FieldSkeleton height={120} />
          <FieldSkeleton height={120} />
        </Stack>
      </Card>

      <Card title="Companies" spacing={4}>
        <Skeleton lines={2} sx={{ mb: 3 }} />
        <Stack direction="row" spacing={2}>
          <Skeleton width={32} height={32} sx={{ borderRadius: '6px' }} />
          <Skeleton width={32} height={32} sx={{ borderRadius: '6px' }} />
        </Stack>
      </Card>
    </Stack>
  </Box>
)

/** Label bar + control box, at the field chrome's real dimensions (§31's 8px gap). */
const FieldSkeleton = ({ height = 49 }: { height?: number }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Skeleton width={110} height={14} sx={{ mb: 2 }} />
    <Skeleton height={height} />
  </Box>
)
